import { SubscriptionStatus } from "@/clients/models/SubscriptionStatus";
import PaywallPage from "@/features/paywall/pages/paywall-page";
import { useGetSubscriptionStatus } from "@/hooks/requests/user/use-get-subscription-status";
import { useGetUser } from "@/hooks/requests/user/use-get-user";
import { useIsSubscriptionEnabled } from "@/hooks/use-is-subscription-enabled";
import { privateRoute } from "@/router/routes";
import { onboardingRoutes } from "@/router/step-routes";
import { aiChatModalOpenAtom, welcomeChatArmedAtom } from "@/stores/chat.store";
import { isPaywallOpenAtom, isSoftPaywallAtom } from "@/stores/paywall.store";
import { reportPaywallFailOpen } from "@/utils/subscription/subscription-status-monitoring";
import { useAuth } from "@clerk/clerk-react";
import { useAtomValue, useSetAtom, useStore } from "jotai";
import { memo, useCallback, useEffect } from "react";
import { useLocation } from "react-router-dom";

const requiresPaywall = (status: SubscriptionStatus, isActive: boolean) => {
  if (isActive) {
    return false;
  }

  return (
    status === SubscriptionStatus.NONE ||
    status === SubscriptionStatus.CANCELLED ||
    status === SubscriptionStatus.EXPIRED ||
    status === SubscriptionStatus.BILLING_ISSUE
  );
};

export const PaywallGuard = memo(function PaywallGuard() {
  const { isLoaded, isSignedIn } = useAuth();
  const { isEnabled: isSubscriptionAndPaywallEnabled, isResolved: isSubscriptionEnabledResolved } =
    useIsSubscriptionEnabled();
  const isPaywallOpen = useAtomValue(isPaywallOpenAtom);
  const setIsPaywallOpen = useSetAtom(isPaywallOpenAtom);
  const setIsSoftPaywall = useSetAtom(isSoftPaywallAtom);
  const openAiChatModal = useSetAtom(aiChatModalOpenAtom);
  const setWelcomeChatArmed = useSetAtom(welcomeChatArmedAtom);
  const store = useStore();
  const location = useLocation();

  // Release the post-onboarding welcome chat the moment we conclude no paywall is
  // required. Guarded by welcomeChatArmedAtom (set only at onboarding finalize) so
  // every other re-run of the effect below is a no-op. Read via the store so this
  // stays out of the effect's dependency array — it must not change when the guard
  // re-evaluates the paywall.
  const releaseWelcomeChatIfArmed = useCallback(() => {
    if (store.get(welcomeChatArmedAtom)) {
      openAiChatModal(true);
      setWelcomeChatArmed(false);
    }
  }, [store, openAiChatModal, setWelcomeChatArmed]);

  const isAuthenticated = isLoaded && isSignedIn;
  const { user, isLoading: isUserLoading } = useGetUser();
  const isOnboardingCompleted = user?.isOnboardingCompleted === true;
  const {
    subscriptionStatus,
    isLoading: isSubscriptionStatusLoading,
    isFetching: isSubscriptionStatusFetching,
    isError: isSubscriptionStatusError,
  } = useGetSubscriptionStatus({
    enabled: isAuthenticated,
  });

  useEffect(() => {
    if (!isSubscriptionEnabledResolved) {
      return;
    }
    if (!isSubscriptionAndPaywallEnabled) {
      setIsPaywallOpen(false);
      releaseWelcomeChatIfArmed();
      return;
    }
    if (!isAuthenticated) {
      setIsPaywallOpen(false);
      return;
    }
    if (isUserLoading) {
      return;
    }
    if (!isOnboardingCompleted) {
      setIsPaywallOpen(false);
      return;
    }

    if (isSubscriptionStatusLoading) {
      return;
    }

    if (!subscriptionStatus) {
      // Authenticated + onboarded, yet we're letting the user in without a
      // positive status. Record it so we can size the structural fix (#3).
      reportPaywallFailOpen({ reason: isSubscriptionStatusError ? "query_error" : "no_data" });
      setIsPaywallOpen(false);
      releaseWelcomeChatIfArmed();
      return;
    }

    const isHardPaywall =
      subscriptionStatus.status === SubscriptionStatus.CANCELLED ||
      subscriptionStatus.status === SubscriptionStatus.EXPIRED ||
      subscriptionStatus.status === SubscriptionStatus.BILLING_ISSUE;
    setIsSoftPaywall(!isHardPaywall);
    const shouldShowPaywall = requiresPaywall(
      subscriptionStatus.status,
      subscriptionStatus.is_active,
    );
    setIsPaywallOpen(shouldShowPaywall);
    // Hold the queued welcome chat behind the paywall: only release once we conclude
    // it should not show. When it should, the subscribe / trial flow re-writes
    // SUBSCRIPTION_STATUS, this effect re-runs, and the release fires then.
    if (!shouldShowPaywall) {
      releaseWelcomeChatIfArmed();
    }
  }, [
    isAuthenticated,
    isSubscriptionStatusError,
    isSubscriptionStatusFetching,
    isSubscriptionStatusLoading,
    isSubscriptionAndPaywallEnabled,
    isSubscriptionEnabledResolved,
    isOnboardingCompleted,
    isUserLoading,
    releaseWelcomeChatIfArmed,
    setIsPaywallOpen,
    setIsSoftPaywall,
    subscriptionStatus,
  ]);

  if (!isSubscriptionEnabledResolved || !isSubscriptionAndPaywallEnabled || !isPaywallOpen) {
    return null;
  }

  // Find if the current path is a private route
  const isPrivateRoute = privateRoute.some((r) => r.path === location.pathname);
  // Find if the current path is an onboarding route
  const isOnboardingRoute = onboardingRoutes.some((r) => r.path === location.pathname);

  if (isPrivateRoute && !isOnboardingRoute) {
    return <PaywallPage />;
  }

  return null;
});
